import { Stack, Typography, useTheme } from "@mui/material";
import {
  CircularLoader,
  Drawer,
  SelectedCheckCircleIcon,
  StatusBadge,
} from "components";
import { useGetAttributes } from "controller";
import { type ReactNode, useState } from "react";
import toast from "react-hot-toast";
import { statusData as STATUSDATA } from "shared/data";
import type {
  MspNotificationEntry,
  TicketStatusAttribute,
  UpdateMspNotificationInput,
} from "shared";
import { getApiErrorMessage } from "utilz/helpers";

/** The fields of a notification entry the status filter reads — same on every endpoint. */
export type StatusFilterEntry = Pick<
  MspNotificationEntry,
  | "notify_type"
  | "notification_status"
  | "notification_sound"
  | "status_ids"
  | "notify_method"
>;

interface TicketStatusSelectDrawerProps {
  onClose: () => void;
  /** The `ticket_status_update` / `update_ticket` entry being edited. */
  entry: StatusFilterEntry | undefined;
  /** Save stays disabled while the entry refetches, so stale roles are never sent. */
  isEntryFetching: boolean;
  isSaving: boolean;
  onSave: (data: UpdateMspNotificationInput["data"]) => Promise<unknown>;
}

// Not offered in this filter, at the product team's request (2026-10-23).
const HIDDEN_STATUS_IDS: ReadonlySet<string> = new Set([
  STATUSDATA.NEW,
  STATUSDATA.MERGED,
  STATUSDATA.REOPEN,
  STATUSDATA.CLOSED,
]);

/** `"all"` = every status, saved as `status_ids: []`; otherwise the chosen ids. */
type Selection = "all" | string[];

const SelectableRow = ({
  isSelected,
  onToggle,
  children,
}: {
  isSelected: boolean;
  onToggle: () => void;
  children: ReactNode;
}) => {
  const theme = useTheme();
  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      role="checkbox"
      aria-checked={isSelected}
      tabIndex={0}
      onClick={onToggle}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onToggle();
        }
      }}
      sx={{
        height: "48px",
        flexShrink: 0,
        p: "12px 24px",
        cursor: "pointer",
        backgroundColor: isSelected
          ? theme.palette.primitive.brand.purple[25]
          : theme.palette.neutral.main,
        // Hover follows ContractPickerDrawer — not in the spec.
        "&:hover": {
          backgroundColor: isSelected
            ? theme.palette.primitive.brand.purple[25]
            : theme.palette.neutral["005"],
        },
      }}
    >
      {children}
      {isSelected && (
        <SelectedCheckCircleIcon
          aria-hidden
          sx={{
            width: "16px",
            height: "16px",
            color: theme.palette.primary.hover,
          }}
        />
      )}
    </Stack>
  );
};

/**
 * Picks which ticket statuses fire a "ticket status updated" notification.
 * Mount it only while open — it loads the status list on mount.
 *
 * "All" is its own choice, like EntityMultiPicker / MultiSelectFieldDrawer: it
 * saves `status_ids: []`, so statuses created later stay included. Picking a
 * status leaves All; unticking the last status, or ticking every status, goes
 * back to All.
 */
export const TicketStatusSelectDrawer = ({
  onClose,
  entry,
  isEntryFetching,
  isSaving,
  onSave,
}: TicketStatusSelectDrawerProps) => {
  const theme = useTheme();
  const { data: statusData, isLoading: isStatusesLoading } = useGetAttributes({
    serviceType: "ticket",
    attributeName: "status",
  });

  // null until the user picks something, so the ticks follow the stored value
  // even when the status list arrives after the drawer opened.
  const [draft, setDraft] = useState<Selection | null>(null);

  const statuses: TicketStatusAttribute[] = (statusData ?? []).filter(
    (status: TicketStatusAttribute) =>
      !status.is_deleted && !HIDDEN_STATUS_IDS.has(status.mapping_id),
  );
  const liveIds = statuses.map((status) => status.mapping_id);
  const storedIds = entry?.status_ids ?? [];
  // Stored ids that aren't offered — deleted (the API rejects them) or hidden
  // above — are dropped, so they are never shown as ticked and never sent
  // back. If none of the stored ids is offered, nothing is ticked and Save
  // stays disabled until the user picks; showing "All" would misstate it.
  const initialSelection: Selection =
    storedIds.length === 0
      ? "all"
      : storedIds.filter((id) => liveIds.includes(id));
  const selection = draft ?? initialSelection;
  const isAllSelected = selection === "all";
  const selectedLiveIds =
    selection === "all" ? [] : liveIds.filter((id) => selection.includes(id));

  const toggleStatus = (statusId: string) => {
    const current = isAllSelected ? [] : selectedLiveIds;
    const next = current.includes(statusId)
      ? current.filter((id) => id !== statusId)
      : [...current, statusId];
    // Nothing ticked, or every status ticked, both mean "All" — `[]` also
    // covers statuses created later, which an explicit list of today's ids
    // would not (backend: "for select all, send [], not every id").
    setDraft(next.length === 0 || next.length === liveIds.length ? "all" : next);
  };

  const handleSave = async () => {
    if (!entry) return;
    try {
      await onSave({
        notification_status: entry.notification_status,
        notification_sound: entry.notification_sound,
        // PATCH replaces methods + roles, so the full current list goes back.
        notify_methods: entry.notify_method
          .filter((method) => method.roles.length > 0)
          .map(({ notify_through, roles }) => ({ notify_through, roles })),
        status_ids: isAllSelected ? [] : selectedLiveIds,
      });
      toast.success("Updated notification settings successfully");
      onClose();
    } catch (error: unknown) {
      toast.error(
        getApiErrorMessage(error, "Failed to update notification settings"),
      );
    }
  };

  return (
    <Drawer
      open
      anchor="right"
      title="Select status"
      onClose={onClose}
      drawerStyle={{ width: "375px" }}
      secondaryButtonText="Cancel"
      onSecondaryButtonClick={onClose}
      primaryButtonText="Save"
      onPrimaryButtonClick={handleSave}
      isPrimaryButtonDisabled={
        !entry ||
        (!isAllSelected && selectedLiveIds.length === 0) ||
        isStatusesLoading ||
        isEntryFetching ||
        isSaving
      }
      isLoading={isSaving}
    >
      {isStatusesLoading ? (
        <Stack p="16px" alignItems="center" justifyContent="center">
          <CircularLoader />
        </Stack>
      ) : (
        <Stack pb="8px">
          <SelectableRow
            isSelected={isAllSelected}
            onToggle={() => setDraft("all")}
          >
            <Typography
              variant="body-medium"
              sx={{ color: theme.palette.lightBg.high }}
            >
              All
            </Typography>
          </SelectableRow>
          {statuses.map((status) => (
            <SelectableRow
              key={status.mapping_id}
              isSelected={selectedLiveIds.includes(status.mapping_id)}
              onToggle={() => toggleStatus(status.mapping_id)}
            >
              <StatusBadge id={status.mapping_id} value={status.status_name} />
            </SelectableRow>
          ))}
        </Stack>
      )}
    </Drawer>
  );
};
